import { useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  MoveHorizontal,
  Grid2X2,
  Hash,
  ChevronDown,
  FileText,
  MousePointer2,
  Eye,
  EyeOff,
  ArrowUpRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { linearUnitLabel, areaUnitLabel } from "./takeoff-helpers";
import {
  aggregateMeasurements,
  aggregateForGroup,
  aggregateByDocument,
  type DrilldownAnnotation,
  type AggregatedTotals,
} from "@/lib/takeoffAggregation";
import type { TakeoffGroup } from "@/hooks/useTakeoffGroups";
import type { TakeoffOwnerType } from "@/hooks/useDocumentAnnotations";

type DrilldownMetric = "linear" | "area" | "count";

interface TakeoffDrilldownSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  metric: DrilldownMetric | null;
  drilldownAnnotations: DrilldownAnnotation[];
  groups: TakeoffGroup[];
  documentMap: Record<string, string>;
  displayUnit: string;
  ownerType: TakeoffOwnerType;
  hiddenGroupIds: Set<string>;
  onAnnotationClick: (params: { docId: string; pageNumber: number; annotationId: string }) => void;
}

const METRIC_LABELS: Record<DrilldownMetric, string> = {
  linear: "Total Linear",
  area: "Total Area",
  count: "Total Count",
};

const METRIC_ICONS: Record<DrilldownMetric, typeof MoveHorizontal> = {
  linear: MoveHorizontal,
  area: Grid2X2,
  count: Hash,
};

function formatMetricValue(
  totals: AggregatedTotals,
  metric: DrilldownMetric,
  displayUnit: string
): string {
  switch (metric) {
    case "linear":
      return `${totals.totalLinear.toFixed(1)} ${linearUnitLabel(displayUnit)}`;
    case "area":
      return `${totals.totalArea.toFixed(1)} ${areaUnitLabel(displayUnit)}`;
    case "count":
      return `${totals.totalCount}`;
  }
}

function annotationTypeIcon(type: string) {
  switch (type) {
    case "measurement_linear":
      return <MoveHorizontal className="h-3 w-3" />;
    case "measurement_area":
      return <Grid2X2 className="h-3 w-3" />;
    case "measurement_count":
      return <Hash className="h-3 w-3" />;
    default:
      return <MousePointer2 className="h-3 w-3" />;
  }
}

function AnnotationRow({
  ann,
  displayUnit,
  ownerType,
  onAnnotationClick,
}: {
  ann: DrilldownAnnotation;
  displayUnit: string;
  ownerType: TakeoffOwnerType;
  onAnnotationClick: TakeoffDrilldownSheetProps["onAnnotationClick"];
}) {
  const valueStr =
    ann.measured_value != null
      ? `${ann.measured_value.toFixed(1)} ${ann.unit}`
      : "—";

  return (
    <button
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 text-left transition-colors group"
      onClick={() =>
        onAnnotationClick({
          docId: ann.document_id,
          pageNumber: ann.page_number,
          annotationId: ann.id,
        })
      }
    >
      <div
        className="h-2.5 w-2.5 rounded-full shrink-0"
        style={{ backgroundColor: ann.color }}
      />
      {annotationTypeIcon(ann.annotation_type)}
      <span className="text-xs truncate flex-1">
        {ann.label || ann.annotation_type.replace("measurement_", "")}
      </span>
      <span className="text-[10px] text-muted-foreground tabular-nums">
        {valueStr}
      </span>
      <span className="text-[10px] text-muted-foreground">p.{ann.page_number}</span>
      {ownerType === "project" && ann.source_estimate_annotation_id && (
        <Badge
          variant="outline"
          className="text-[8px] px-1 py-0 border-blue-300 text-blue-600"
        >
          Est
        </Badge>
      )}
      <ArrowUpRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
    </button>
  );
}

export function TakeoffDrilldownSheet({
  open,
  onOpenChange,
  metric,
  drilldownAnnotations,
  groups,
  documentMap,
  displayUnit,
  ownerType,
  hiddenGroupIds,
  onAnnotationClick,
}: TakeoffDrilldownSheetProps) {
  const [visibilityMode, setVisibilityMode] = useState<"all" | "visible">("all");

  // Filter by metric type
  const metricAnnotations = useMemo(() => {
    if (!metric) return drilldownAnnotations;
    const typeMap: Record<DrilldownMetric, string> = {
      linear: "measurement_linear",
      area: "measurement_area",
      count: "measurement_count",
    };
    return drilldownAnnotations.filter(
      (a) => a.annotation_type === typeMap[metric]
    );
  }, [drilldownAnnotations, metric]);

  // Apply visibility filter
  const filteredAnnotations = useMemo(() => {
    if (visibilityMode === "all") return metricAnnotations;
    return metricAnnotations.filter((ann) => {
      const groupKey = ann.takeoff_group_id ?? "__unassigned__";
      return !hiddenGroupIds.has(groupKey);
    });
  }, [metricAnnotations, visibilityMode, hiddenGroupIds]);

  const overallTotals = useMemo(
    () => aggregateMeasurements(filteredAnnotations, displayUnit),
    [filteredAnnotations, displayUnit]
  );

  const byDocument = useMemo(
    () => aggregateByDocument(filteredAnnotations, displayUnit, documentMap),
    [filteredAnnotations, displayUnit, documentMap]
  );

  if (!metric) return null;

  const MetricIcon = METRIC_ICONS[metric];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[380px] sm:max-w-[420px] p-0 flex flex-col">
        <SheetHeader className="px-4 pt-4 pb-3 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-sm">
            <MetricIcon className="h-4 w-4 text-primary" />
            {METRIC_LABELS[metric]}
            <span className="font-bold tabular-nums ml-auto">
              {formatMetricValue(overallTotals, metric, displayUnit)}
            </span>
          </SheetTitle>
          {/* Visibility toggle */}
          <div className="flex gap-1 mt-2">
            <Button
              variant={visibilityMode === "all" ? "default" : "outline"}
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => setVisibilityMode("all")}
            >
              <Eye className="h-3 w-3 mr-1" />
              All
            </Button>
            <Button
              variant={visibilityMode === "visible" ? "default" : "outline"}
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => setVisibilityMode("visible")}
            >
              <EyeOff className="h-3 w-3 mr-1" />
              Visible only
            </Button>
          </div>
        </SheetHeader>

        <Tabs defaultValue="by-group" className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-4 mt-2">
            <TabsTrigger value="by-group" className="text-xs">
              By Group
            </TabsTrigger>
            <TabsTrigger value="by-document" className="text-xs">
              By Document
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            <TabsContent value="by-group" className="p-3 space-y-1 mt-0">
              {groups.map((group) => {
                const groupAnns = filteredAnnotations.filter(
                  (a) => a.takeoff_group_id === group.id
                );
                if (groupAnns.length === 0) return null;
                const totals = aggregateMeasurements(groupAnns, displayUnit);
                return (
                  <GroupSection
                    key={group.id}
                    label={group.name}
                    color={group.color}
                    count={groupAnns.length}
                    totals={totals}
                    metric={metric}
                    displayUnit={displayUnit}
                    annotations={groupAnns}
                    ownerType={ownerType}
                    documentMap={documentMap}
                    onAnnotationClick={onAnnotationClick}
                  />
                );
              })}
              {/* Unassigned */}
              {(() => {
                const unAnns = filteredAnnotations.filter(
                  (a) => !a.takeoff_group_id
                );
                if (unAnns.length === 0) return null;
                const totals = aggregateMeasurements(unAnns, displayUnit);
                return (
                  <GroupSection
                    label="Unassigned"
                    color="hsl(var(--muted-foreground))"
                    count={unAnns.length}
                    totals={totals}
                    metric={metric}
                    displayUnit={displayUnit}
                    annotations={unAnns}
                    ownerType={ownerType}
                    documentMap={documentMap}
                    onAnnotationClick={onAnnotationClick}
                  />
                );
              })()}
            </TabsContent>

            <TabsContent value="by-document" className="p-3 space-y-1 mt-0">
              {byDocument.map((docSub) => (
                <Collapsible key={docSub.documentId}>
                  <CollapsibleTrigger className="flex items-center gap-2 w-full px-2 py-2 rounded-md hover:bg-muted/50 text-left">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs font-medium truncate flex-1">
                      {docSub.documentName}
                    </span>
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      {formatMetricValue(docSub.totals, metric, displayUnit)}
                    </span>
                    <Badge
                      variant="secondary"
                      className="text-[9px] px-1 py-0"
                    >
                      {docSub.annotationCount}
                    </Badge>
                    <ChevronDown className="h-3 w-3 text-muted-foreground transition-transform [&[data-state=open]]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-6 space-y-0.5 pb-1">
                    {docSub.annotations.map((ann) => (
                      <AnnotationRow
                        key={ann.id}
                        ann={ann}
                        displayUnit={displayUnit}
                        ownerType={ownerType}
                        onAnnotationClick={onAnnotationClick}
                      />
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function GroupSection({
  label,
  color,
  count,
  totals,
  metric,
  displayUnit,
  annotations,
  ownerType,
  documentMap,
  onAnnotationClick,
}: {
  label: string;
  color: string;
  count: number;
  totals: AggregatedTotals;
  metric: DrilldownMetric;
  displayUnit: string;
  annotations: DrilldownAnnotation[];
  ownerType: TakeoffOwnerType;
  documentMap: Record<string, string>;
  onAnnotationClick: TakeoffDrilldownSheetProps["onAnnotationClick"];
}) {
  // Sub-group by document
  const byDoc = aggregateByDocument(annotations, displayUnit, documentMap);

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex items-center gap-2 w-full px-2 py-2 rounded-md hover:bg-muted/50 text-left">
        <div
          className="h-3 w-3 rounded-full shrink-0"
          style={{ backgroundColor: color }}
        />
        <span className="text-xs font-medium truncate flex-1">{label}</span>
        <span className="text-[10px] text-muted-foreground tabular-nums">
          {formatMetricValue(totals, metric, displayUnit)}
        </span>
        <Badge variant="secondary" className="text-[9px] px-1 py-0">
          {count}
        </Badge>
        <ChevronDown className="h-3 w-3 text-muted-foreground transition-transform [&[data-state=open]]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-5 space-y-1 pb-1">
        {byDoc.map((docSub) => (
          <Collapsible key={docSub.documentId}>
            <CollapsibleTrigger className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-muted/30 text-left">
              <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
              <span className="text-[11px] truncate flex-1">
                {docSub.documentName}
              </span>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {docSub.annotationCount}
              </span>
              <ChevronDown className="h-2.5 w-2.5 text-muted-foreground" />
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-4 space-y-0.5">
              {docSub.annotations.map((ann) => (
                <AnnotationRow
                  key={ann.id}
                  ann={ann}
                  displayUnit={displayUnit}
                  ownerType={ownerType}
                  onAnnotationClick={onAnnotationClick}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}
